import React, { useState, useEffect } from "react";
import "./App.css";
import { useNavigate, useLocation } from "react-router-dom"; 
import lawImage from "./media/law.jpg";
import logo from "./media/logo.png";
import QRCode from "./media/qr_code.svg";

// ==========================================
// 🔴 CONFIGURATION: PASTE YOUR LINK HERE 🔴
// ==========================================
const BOT_DIRECT_LINK = "https://samzeus-merkel.hf.space"; 


// --- INTERNAL CHAT WIDGET COMPONENT ---
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={widgetStyles.container}>
      {/* Chat Window */}
      <div style={{...widgetStyles.window, display: isOpen ? 'flex' : 'none'}}>
        <div style={widgetStyles.header}>
          <span>⚖️ Merkel AI</span>
          <button onClick={() => setIsOpen(false)} style={widgetStyles.closeBtn}>×</button>
        </div>
        <iframe 
          src={BOT_DIRECT_LINK}
          width="100%" 
          height="100%" 
          frameBorder="0"
          title="AI Chat"
        />
      </div>

      {/* Floating Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        style={widgetStyles.fab}
      >
        {isOpen ? "Close" : "💬 Chat"}
      </button>
    </div>
  );
};

// Styles for the Widget
const widgetStyles = {
  container: {
    position: "fixed",
    bottom: "30px",
    right: "30px",
    zIndex: 999999, // Max priority
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  window: {
    width: "350px",
    height: "500px",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 5px 20px rgba(0,0,0,0.2)",
    marginBottom: "15px",
    border: "1px solid #ddd",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    backgroundColor: "#1a1a1a",
    color: "#C49A6C",
    padding: "15px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: "bold",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "white",
    fontSize: "20px",
    cursor: "pointer",
  },
  fab: {
    backgroundColor: "#9A6533",
    color: "white",
    border: "2px solid white",
    borderRadius: "50px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    transition: "transform 0.2s",
  }
};

// --- DATA CONSTANTS ---
const NAV_ITEMS = [
  { label: "About Us", path: "/about", type: "page" }, 
  { label: "Our Team", path: "/team", type: "page" },
  { label: "Services", path: "#services", type: "scroll" }, 
  { label: "Contact", path: "/contact", type: "page" }
];

const SERVICES = [
  "Commercial Law", "Corporate Law", "Real Estate Consultancy",
  "Intellectual Property", "Insolvency Law", "Litigation",
  "Tax Law", "Pro Bono Briefs", "Probate & Succession",
  "Arbitration & Dispute Resolution"
];

const FEATURES = [
  { icon: "fa-headset", title: "Available", desc: "Prompt updates and swift responses to all client inquiries." },
  { icon: "fa-users", title: "Client-Centric", desc: "Tailored strategies working closely to achieve your unique outcomes." },
  { icon: "fa-hand-holding-usd", title: "High Value", desc: "Top-tier expertise and long-term experience in every piece of advice." }
];

// --- MAIN APP COMPONENT ---
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigation = (item) => {
    setMenuOpen(false); 
    if (item.type === "scroll") {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const element = document.querySelector(item.path);
          if (element) element.scrollIntoView({ behavior: "smooth" });
        }, 100); 
      } else {
        const element = document.querySelector(item.path);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(item.path);
      window.scrollTo(0, 0); 
    }
  };

  return (
    <div className="App">
      
      {/* ✅ WIDGET IS EMBEDDED HERE - GUARANTEED TO LOAD */}
      <ChatWidget />

      {/* Navigation Overlay */}
      <div 
        className={`overlay ${menuOpen ? "active" : ""}`} 
        onClick={() => setMenuOpen(false)} 
      />

      {/* Header */}
      <header className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="container nav-container">
          <div className="logo-wrapper" onClick={() => navigate("/")}>
            <img src={logo} alt="Merkel & Associates" className="logo-img" />
          </div>
          
          <div className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            <div className={`hamburger ${menuOpen ? "open" : ""}`}>
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      </header>
      
      {/* Side Menu */}
      <nav className={`side-menu ${menuOpen ? "open" : ""}`}>
        <ul className="nav-links">
          {NAV_ITEMS.map((item, index) => (
            <li key={index} style={{ animationDelay: `${index * 0.1}s` }}>
              <a 
                href={item.path} 
                onClick={(e) => { 
                  e.preventDefault(); 
                  handleNavigation(item); 
                }}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg" style={{ backgroundImage: `url(${lawImage})` }}></div>
        <div className="hero-content">
          <h1 className="fade-in-up">Excellence in Legal Practice</h1>
          <p className="fade-in-up delay-1">Effective Systems. Continuous Growth. Innovative Strategies.</p>
          <button className="btn-primary fade-in-up delay-2" onClick={() => navigate("/contact")}>
            Start a Consultation
          </button>
        </div>
      </section>

      {/* Why Us */}
      <section className="section commitment">
        <div className="container">
          <div className="section-header">
            <h2>Our Commitment</h2>
            <div className="divider"></div>
          </div>
          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div className="feature-card" key={i}>
                <div className="icon-box">
                  <i className={`fas ${f.icon}`}></i>
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section services-section">
        <div className="container">
          <div className="section-header">
            <h2>Areas of Practice</h2>
            <div className="divider"></div>
            <p className="subtitle">Comprehensive legal solutions for modern challenges.</p>
          </div>
          <div className="services-grid">
            {SERVICES.map((service, index) => (
              <div className="service-card" key={index}>
                <span className="service-icon">§</span>
                <span>{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer-section">
        <div className="container">
          <div className="footer-grid">
            
            <div className="footer-col">
              <h3>Get in Touch</h3>
              <div className="contact-row">
                <i className="fas fa-envelope"></i>
                <a href="mailto:info@merkel.co.ke">info@merkel.co.ke</a>
              </div>
              <div className="contact-row">
                <i className="fas fa-phone"></i>
                <a href="tel:+254743933520">+254 743 933 520</a>
              </div>
              
              <div className="qr-wrapper">
                <img src={QRCode} alt="Scan for details" />
              </div>
            </div>

            <div className="footer-col">
              <h3>Quick Access</h3>
              <ul className="footer-links">
                <li>
                    <a href="https://www.indexregistrars.com/" target="_blank" rel="noopener noreferrer">
                        Index Registrars
                    </a>
                </li>
                <li><a onClick={() => navigate("/Support")}>Support Services</a></li>
                <li><a onClick={() => navigate("/Focus")}>Focus Industry</a></li>
                <li><a onClick={() => navigate("/Future")}>Future Outlook</a></li>
                <li><a onClick={() => navigate("/Assurance")}>Quality Assurance</a></li>
                <li><a onClick={() => navigate("/Confidentiality")}>Confidentiality</a></li>
                <li><a onClick={() => navigate("/Indemnity")}>Professional Indemnity</a></li>
              </ul>
            </div>

            <div className="footer-col map-col">
              <h3>Visit Us</h3>
              <div className="map-frame">
                <iframe
                  title="Firm Location"
                  src="https://maps.google.com/maps?q=Nairobi&t=&z=13&ie=UTF8&iwloc=&output=embed" 
                  width="100%" height="100%" frameBorder="0" allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>© 2025 Merkel & Associates Advocates. Excellence Defined.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;